// src/components/modals/FileModals/FileViewerEndowment.jsx
import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { Modal, Button, Spinner, Alert, Form } from 'react-bootstrap';
import { getFileIconInfo } from '../../../services/file.service';
import '../../../styles/components/fileViewer.css';
import useModalHistory from '../../../hooks/useModalHistory';

const FileViewerEndowment = ({ show, onHide, fileData, onRemove }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [fileContent, setFileContent] = useState(null);
  const [textContent, setTextContent] = useState('');
  const [imageError, setImageError] = useState(false);
  const [videoError, setVideoError] = useState(false);
  const [pdfUrl, setPdfUrl] = useState(null);
  const [zoom, setZoom] = useState(100);
  const [fontSize, setFontSize] = useState(16); // Increased default font size
  const [wordWrap, setWordWrap] = useState(true);

  const videoRef = useRef(null);
  const audioRef = useRef(null);

  useModalHistory(show, onHide, 'endowment-file-view-modal');

  // Helper function to get language for syntax highlighting
  const getLanguageFromExtension = (fileName) => {
    const extension = fileName.split('.').pop().toLowerCase();
    const languageMap = {
      'js': 'javascript',
      'jsx': 'javascript',
      'ts': 'typescript',
      'tsx': 'typescript',
      'py': 'python',
      'java': 'java',
      'c': 'c',
      'cpp': 'cpp',
      'cs': 'csharp',
      'php': 'php',
      'rb': 'ruby',
      'go': 'go',
      'rs': 'rust',
      'swift': 'swift',
      'kt': 'kotlin',
      'scala': 'scala',
      'html': 'html',
      'css': 'css',
      'scss': 'scss',
      'sass': 'sass',
      'less': 'less',
      'xml': 'xml',
      'json': 'json',
      'yaml': 'yaml',
      'yml': 'yaml',
      'toml': 'toml',
      'ini': 'ini',
      'cfg': 'ini',
      'conf': 'ini',
      'sh': 'bash',
      'bash': 'bash',
      'zsh': 'bash',
      'fish': 'bash',
      'ps1': 'powershell',
      'sql': 'sql',
      'md': 'markdown',
      'dockerfile': 'dockerfile',
      'r': 'r',
      'matlab': 'matlab',
      'm': 'matlab'
    };
    return languageMap[extension] || 'text';
  };

  // Check if file type is code
  const isCodeFile = (fileName, contentType) => {
    const extension = fileName.split('.').pop().toLowerCase();
    const codeExtensions = [
      'js', 'jsx', 'ts', 'tsx', 'py', 'java', 'c', 'cpp', 'cs', 'php', 'rb', 'go', 'rs',
      'swift', 'kt', 'scala', 'html', 'css', 'scss', 'sass', 'less', 'xml', 'json',
      'yaml', 'yml', 'toml', 'ini', 'cfg', 'conf', 'sh', 'bash', 'zsh', 'fish', 'ps1',
      'sql', 'md', 'dockerfile', 'r', 'matlab', 'm'
    ];
    return codeExtensions.includes(extension) || contentType?.includes('javascript') || contentType?.includes('json');
  };

  // Reset state when modal opens/closes
  useEffect(() => {
    if (show) {
      setLoading(true);
      setError(null);
      setImageError(false);
      setVideoError(false);
      setZoom(100);
      setFontSize(16); // Increased default
      setWordWrap(true);
    } else {
      setFileContent(null);
      setTextContent('');
      if (pdfUrl) {
        URL.revokeObjectURL(pdfUrl);
        setPdfUrl(null);
      }
    }
  }, [show]);

  // Process file data when it changes
  useEffect(() => {
    if (!fileData) return;

    setLoading(true);
    setError(null);

    try {

      if (!fileData.contentType) {
        setError('File content type not available');
        setLoading(false);
        return;
      }

      // Set up file content object
      const content = {
        type: fileData.contentType,
        url: fileData.url,
        name: fileData.fileName || fileData.name,
        blob: fileData.blob
      };

      setFileContent(content);

      // Handle text content - check if it's already provided or extract from blob
      if (fileData.textContent) {
        // Text content already provided (like in the static version)
        setTextContent(fileData.textContent);
        setLoading(false);
      } else if (fileData.contentType.startsWith('text/') || isCodeFile(content.name, fileData.contentType)) {
        // Try to extract text from blob if available
        if (fileData.blob) {
          fileData.blob.text().then(text => {
            setTextContent(text);
            setLoading(false);
          }).catch(err => {
            console.error('Error reading text from blob:', err);
            setTextContent('Could not read text content');
            setLoading(false);
          });
        } else {
          setTextContent('No text content available');
          setLoading(false);
        }
      } else if (fileData.contentType === 'application/pdf' && fileData.blob) {
        // Create PDF URL
        const url = URL.createObjectURL(fileData.blob);
        setPdfUrl(url);
        setLoading(false);
      } else {
        setLoading(false);
      }
    } catch (err) {
      console.error('Error processing file data:', err);
      setError(`Error processing file: ${err.message}`);
      setLoading(false);
    }
  }, [fileData]);

  // Clean up object URLs when component unmounts
  useEffect(() => {
    return () => {
      if (pdfUrl) {
        URL.revokeObjectURL(pdfUrl);
      }
    };
  }, [pdfUrl]);

  // Handle video error
  const handleVideoError = (e) => {
    console.error('Video error:', e);
    setVideoError(true);
  };

  // Handle image error
  const handleImageError = (e) => {
    console.error('Image error:', e);
    setImageError(true);
  };

  // Handle remove button click
  const handleRemove = () => {
    if (onRemove && fileData) {
      onRemove(fileData.fileName || fileData.name);
    }
  };

  // Render controls for text/code files
  const renderTextControls = () => (
    <div className="file-viewer-text-controls">
      <div className="d-flex align-items-center">
        <label className="text-white me-3 mb-0 fw-bold">Font Size:</label>
        <Form.Range
          min="12"
          max="28"
          value={fontSize}
          onChange={(e) => setFontSize(parseInt(e.target.value))}
          style={{ width: '120px' }}
          className="me-3"
        />
        <span className="text-white me-4 fw-bold">{fontSize}px</span>
      </div>
      <Form.Check
        type="checkbox"
        id="word-wrap"
        label="Word Wrap"
        checked={wordWrap}
        onChange={(e) => setWordWrap(e.target.checked)}
        className="text-white fw-bold"
      />
    </div>
  );

  // Render controls for images
  const renderImageControls = () => (
    <div className="file-viewer-image-controls">
      <Button variant="outline-light" size="sm" onClick={() => setZoom(Math.max(10, zoom - 10))}>
        <i className="fas fa-search-minus"></i>
      </Button>
      <span className="text-white mx-3 fw-bold">{zoom}%</span>
      <Button variant="outline-light" size="sm" onClick={() => setZoom(Math.min(300, zoom + 10))} className="me-3">
        <i className="fas fa-search-plus"></i>
      </Button>
      <Button variant="outline-light" size="sm" onClick={() => setZoom(100)}>
        Reset
      </Button>
    </div>
  );

  // Handle file type-specific rendering
  const renderFileContent = () => {
    if (loading) {
      return (
        <div className="file-viewer-loading">
          <Spinner animation="border" role="status" variant="light" style={{ width: '3rem', height: '3rem' }}>
            <span className="sr-only">Loading...</span>
          </Spinner>
          <p className="mt-3 text-white fs-5">Loading file content...</p>
        </div>
      );
    }

    if (error) {
      return (
        <Alert variant="danger" className="my-3 fs-5">
          <i className="fas fa-exclamation-triangle me-2"></i>
          {error}
        </Alert>
      );
    }

    if (!fileContent) {
      return (
        <div className="file-viewer-no-content">
          <i className="fas fa-file fa-4x text-muted mb-3"></i>
          <p className="text-white fs-4">No file selected or file could not be loaded</p>
        </div>
      );
    }

    // Handle images
    if (fileContent.type.startsWith('image/')) {
      if (imageError) {
        return (
          <div className="file-viewer-error">
            <i className="fas fa-image fa-4x text-danger mb-3"></i>
            <p className="text-white fs-4 mb-4">Failed to load image</p>
            <Button variant="danger" size="lg" onClick={handleRemove}>
              <i className="fas fa-trash me-2"></i>Remove from Endowment
            </Button>
          </div>
        );
      }

      return (
        <div className="file-viewer-image-container">
          {renderImageControls()}
          <div className="file-viewer-image-wrapper">
            <img
              src={fileContent.url}
              alt={fileContent.name}
              className="file-viewer-image"
              style={{
                transform: `scale(${zoom / 100})`,
                transformOrigin: 'center',
                transition: 'transform 0.2s'
              }}
              onLoad={() => setLoading(false)}
              onError={handleImageError}
            />
          </div>
        </div>
      );
    }

    // Handle videos
    if (fileContent.type.startsWith('video/')) {
      if (videoError) {
        return (
          <div className="file-viewer-error">
            <i className="fas fa-video fa-4x text-danger mb-3"></i>
            <p className="text-white fs-4 mb-4">Video format not supported or failed to load</p>
            <Button variant="danger" size="lg" onClick={handleRemove}>
              <i className="fas fa-trash me-2"></i>Remove from Endowment
            </Button>
          </div>
        );
      }

      return (
        <div className="file-viewer-video-container">
          <video
            ref={videoRef}
            src={fileContent.url}
            controls
            className="file-viewer-video"
            onLoadedData={() => setLoading(false)}
            onError={handleVideoError}
            preload="metadata"
          >
            Your browser does not support the video tag.
          </video>
        </div>
      );
    }

    // Handle audio
    if (fileContent.type.startsWith('audio/')) {
      return (
        <div className="file-viewer-audio-container">
          <i className="fas fa-music fa-4x text-info mb-4"></i>
          <h4 className="text-white mb-4">{fileContent.name}</h4>
          <audio
            ref={audioRef}
            src={fileContent.url}
            controls
            className="file-viewer-audio"
            onLoadedData={() => setLoading(false)}
            preload="metadata"
          >
            Your browser does not support the audio tag.
          </audio>
        </div>
      );
    }

    // Handle text files and code files
    if (fileContent.type.startsWith('text/') || isCodeFile(fileContent.name, fileContent.type)) {
      const language = getLanguageFromExtension(fileContent.name);
      const isCode = isCodeFile(fileContent.name, fileContent.type);

      return (
        <div className="file-viewer-text-container">
          {renderTextControls()}
          <div className="file-viewer-text-content">
            <div className="file-viewer-text-header">
              <span className="text-muted">
                <i className={`fas ${isCode ? 'fa-code' : 'fa-file-alt'} me-2`}></i>
                {fileContent.name} {isCode && `(${language})`}
              </span>
              <span className="text-muted">
                {textContent.length} characters, {textContent.split('\n').length} lines
              </span>
            </div>
            <pre
              className="file-viewer-text-pre"
              style={{
                whiteSpace: wordWrap ? 'pre-wrap' : 'pre',
                wordBreak: wordWrap ? 'break-word' : 'normal',
                fontSize: `${fontSize}px`,
                lineHeight: '1.6'
              }}
            >
              {textContent || 'No text content available'}
            </pre>
          </div>
        </div>
      );
    }

    // Handle PDFs
    if (fileContent.type === 'application/pdf') {
      return (
        <div className="file-viewer-pdf-container">
          <div className="file-viewer-pdf-controls">
            <Button
              variant="primary"
              size="lg"
              onClick={() => window.open(pdfUrl || fileContent.url, '_blank')}
              className="me-3"
            >
              <i className="fas fa-external-link-alt me-2"></i>
              Open in New Tab
            </Button>
            <Button variant="danger" size="lg" onClick={handleRemove}>
              <i className="fas fa-trash me-2"></i>
              Remove from Endowment
            </Button>
          </div>

          {pdfUrl && (
            <div className="file-viewer-pdf-frame">
              <iframe
                src={`${pdfUrl}#toolbar=1&navpanes=1&scrollbar=1&page=1&view=FitH`}
                width="100%"
                height="100%"
                style={{ border: 'none' }}
                title={fileContent.name}
              />
            </div>
          )}
        </div>
      );
    }

    // Handle Office documents
    if (fileContent.type.includes('spreadsheetml') ||
      fileContent.type.includes('presentationml') ||
      fileContent.type.includes('wordprocessingml') ||
      fileContent.type === 'application/msword' ||
      fileContent.type === 'application/vnd.ms-excel' ||
      fileContent.type === 'application/vnd.ms-powerpoint') {

      return (
        <div className="file-viewer-office-container">
          <i className="fas fa-file-alt fa-4x text-info mb-4"></i>
          <h4 className="text-white mb-4">{fileContent.name}</h4>
          <p className="text-muted mb-4 fs-5">Office documents can be opened in a new tab</p>
          <div>
            <Button
              variant="primary"
              size="lg"
              onClick={() => window.open(fileContent.url, '_blank')}
              className="me-3"
            >
              <i className="fas fa-external-link-alt me-2"></i>
              Open Document
            </Button>
            <Button variant="danger" size="lg" onClick={handleRemove}>
              <i className="fas fa-trash me-2"></i>
              Remove from Endowment
            </Button>
          </div>
        </div>
      );
    }

    // For other file types, show a generic preview with file info
    const { iconClass, iconColor } = getFileIconInfo({ name: fileContent.name, type: 'file' });

    return (
      <div className="file-viewer-generic-container">
        <i className={`fas ${iconClass} fa-5x mb-4`} style={{ color: iconColor }}></i>
        <h4 className="text-white mb-3">{fileContent.name}</h4>
        <p className="text-muted mb-2 fs-5">Type: {fileContent.type}</p>
        {fileContent.blob && (
          <p className="text-muted mb-4 fs-5">Size: {(fileContent.blob.size / 1024).toFixed(1)} KB</p>
        )}
        <p className="text-muted mb-4 fs-5">This file type cannot be previewed in the browser</p>
        <Button
          variant="danger"
          onClick={handleRemove}
          size="lg"
        >
          <i className="fas fa-trash me-2"></i>
          Remove from Endowment
        </Button>
      </div>
    );
  };

  return (
    <Modal
      show={show}
      onHide={onHide}
      dialogClassName="file-viewer-modal-dialog"
      contentClassName="file-viewer-modal-content"
      backdrop="static"
      scrollable={false}
    >
      <Modal.Header closeButton className="file-viewer-modal-header">
        <Modal.Title className="d-flex align-items-center fs-3">
          <i className="fas fa-eye me-3"></i>
          {fileData?.fileName || fileData?.name || 'File Viewer'}
        </Modal.Title>
      </Modal.Header>

      <Modal.Body className="file-viewer-modal-body">
        <div className="file-viewer-container">
          {renderFileContent()}
        </div>
      </Modal.Body>

      <Modal.Footer className="file-viewer-modal-footer">
        <div className="file-viewer-button-container">
          <Button
            variant="danger"
            size="lg"
            className="file-viewer-action-btn"
            onClick={handleRemove}
            disabled={!fileData}
          >
            <i className="fas fa-trash me-2"></i> Remove from Endowment
          </Button>
          
          <Button
            variant="secondary"
            size="lg"
            className="file-viewer-close-btn"
            onClick={onHide}
          >
            <i className="fas fa-times me-2"></i>Close
          </Button>
        </div>
      </Modal.Footer>
    </Modal>
  );
};

FileViewerEndowment.propTypes = {
  show: PropTypes.bool.isRequired,
  onHide: PropTypes.func.isRequired,
  fileData: PropTypes.shape({
    fileName: PropTypes.string,
    name: PropTypes.string,
    contentType: PropTypes.string,
    url: PropTypes.string,
    blob: PropTypes.object,
    textContent: PropTypes.string
  }),
  onRemove: PropTypes.func.isRequired
};

export default FileViewerEndowment;